import { conditionRuleTypes, conditionOperators } from '../formDetail/properties/fragments/conditionConstants';

/**
 * DUPLICATED from `evaluateConditions` in @therapylake/package
 * (form-builder/constants/conditions.ts), which the PDF renderer (reportMicroservice) uses to leave
 * hidden fields out of a note's PDF. It cannot be imported here: the `prebuild` runs package's
 * `build:frontend`, which emits only `src/sentry-shared/**`. Keep the copies in sync.
 */

/**
 * Normalise a field value to a flat array of strings for comparison.
 * Handles: string, { name, id }, [{ name, id }], null/undefined.
 */
const normaliseValue = (value) => {
    if (value === null || value === undefined) return [];
    if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
    if (Array.isArray(value)) {
        return value
            .map((v) => (typeof v === 'string' ? v : v?.name || v?.value || ''))
            .filter(Boolean);
    }
    if (typeof value === 'object') {
        return [value?.name || value?.value || ''].filter(Boolean);
    }
    return [String(value)];
};

/**
 * Check whether a single condition is satisfied.
 */
const evaluateSingleCondition = (condition, allAttributes) => {
    const { fieldId, operator, value: conditionValue } = condition;
    if (!fieldId || !operator) return false;

    const sourceField = allAttributes.find((attr) => attr?.id === fieldId);
    if (!sourceField) return false;

    const fieldValues = normaliseValue(sourceField?.value);
    const isEmpty = fieldValues.length === 0;

    switch (operator) {
        case conditionOperators.IS_EMPTY:
            return isEmpty;

        case conditionOperators.IS_NOT_EMPTY:
            return !isEmpty;

        case conditionOperators.IS: {
            const target = typeof conditionValue === 'string' ? conditionValue : conditionValue?.name || conditionValue?.value || '';
            return fieldValues.some((v) => v === target);
        }

        case conditionOperators.IS_NOT: {
            const target = typeof conditionValue === 'string' ? conditionValue : conditionValue?.name || conditionValue?.value || '';
            return !fieldValues.some((v) => v === target);
        }

        case conditionOperators.IS_ONE_OF: {
            const targets = Array.isArray(conditionValue)
                ? conditionValue.map((v) => (typeof v === 'string' ? v : v?.name || v?.value || ''))
                : [];
            return fieldValues.some((v) => targets.includes(v));
        }

        case conditionOperators.IS_NONE_OF: {
            const targets = Array.isArray(conditionValue)
                ? conditionValue.map((v) => (typeof v === 'string' ? v : v?.name || v?.value || ''))
                : [];
            return !fieldValues.some((v) => targets.includes(v));
        }

        default:
            return false;
    }
};

/**
 * Check whether all conditions in a rule are satisfied (AND logic).
 */
const evaluateRule = (rule, allAttributes) => {
    if (!rule?.conditions?.length) return false;
    return rule.conditions.every((cond) => evaluateSingleCondition(cond, allAttributes));
};

/**
 * Evaluates all conditional-logic rules for an attribute against the current
 * form values.
 *
 * @param {Array|undefined} rules  – `properties.conditions` from the attribute
 * @param {Array}           allAttributes – all attributes in the section, each
 *                          carrying its current `value`
 * @returns {{ visible: boolean, required: boolean, readOnly: boolean }}
 */
export const evaluateConditions = (rules, allAttributes) => {
    const result = { visible: true, required: false, readOnly: false };

    if (!rules?.length) return result;

    // Separate rules by type
    const showRules = rules.filter((r) => r.type === conditionRuleTypes.SHOW_IF);
    const hideRules = rules.filter((r) => r.type === conditionRuleTypes.HIDE_IF);
    const requireRules = rules.filter((r) => r.type === conditionRuleTypes.REQUIRE_IF);
    const readonlyRules = rules.filter((r) => r.type === conditionRuleTypes.READONLY_IF);

    // Visibility: SHOW_IF rules — field is visible ONLY if at least one SHOW_IF matches
    if (showRules.length > 0) {
        result.visible = showRules.some((rule) => evaluateRule(rule, allAttributes));
    }

    // Visibility: HIDE_IF rules — field is hidden if any HIDE_IF matches
    if (hideRules.length > 0 && result.visible) {
        const shouldHide = hideRules.some((rule) => evaluateRule(rule, allAttributes));
        if (shouldHide) result.visible = false;
    }

    // Validation: REQUIRE_IF — additive, field becomes required if any rule matches
    if (requireRules.length > 0) {
        result.required = requireRules.some((rule) => evaluateRule(rule, allAttributes));
    }

    // Validation: READONLY_IF — additive, field becomes readOnly if any rule matches
    if (readonlyRules.length > 0) {
        result.readOnly = readonlyRules.some((rule) => evaluateRule(rule, allAttributes));
    }

    return result;
};
